import { useRef, useState } from 'react';
import { ERAS } from '../game/constants';
import { useGameState } from '../game/useGameState';
import { formatNumber } from '../game/utils';
import { ResourceBar } from './ResourceBar';
import { WorldOrb } from './WorldOrb';
import { UpgradePanel } from './UpgradePanel';

export function GameView() {
  const { state, energyPerSecond, energyPerClick, offlinePopup, setOfflinePopup, handleClick, buyUpgrade, buyUpgradeBulk, resetGame } =
    useGameState();
  const [resetConfirm, setResetConfirm] = useState(false);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleReset() {
    if (resetConfirm) {
      if (resetTimerRef.current !== null) clearTimeout(resetTimerRef.current);
      resetGame();
      setResetConfirm(false);
    } else {
      setResetConfirm(true);
      resetTimerRef.current = setTimeout(() => setResetConfirm(false), 3000);
    }
  }

  const currentEra = ERAS[state.currentEraIndex];
  if (!currentEra) return null;

  return (
    <div className="flex min-h-screen flex-col bg-gray-950 bg-[radial-gradient(ellipse_at_top,_#1e293b_0%,_#030712_70%)]">
      <ResourceBar
        energy={state.energy}
        totalEnergyEarned={state.totalEnergyEarned}
        energyPerSecond={energyPerSecond}
        currentEraIndex={state.currentEraIndex}
      />

      <main id="main-content" className="mx-auto w-full max-w-5xl flex-1 px-4 py-8 md:py-12">
        <div className="grid grid-cols-1 items-start gap-8 md:grid-cols-2">
          <div className="flex flex-col items-center justify-center py-6">
            <WorldOrb
              era={currentEra}
              onClick={handleClick}
              energyPerClick={energyPerClick}
              clickCount={state.clickCount}
            />
          </div>

          <div className="rounded-2xl border border-gray-700/50 bg-gray-900/40 p-5 backdrop-blur-sm">
            <UpgradePanel
              energy={state.energy}
              ownedUpgrades={state.ownedUpgrades}
              currentEraIndex={state.currentEraIndex}
              onBuy={buyUpgrade}
              onBuyBulk={buyUpgradeBulk}
            />
          </div>
        </div>

        <div className="mt-10 flex justify-center">
          <button
            onClick={handleReset}
            className="rounded-lg border border-gray-800 px-4 py-1.5 text-xs text-gray-600 transition-all hover:border-red-900/50 hover:text-red-500"
          >
            {resetConfirm ? 'Click again to confirm reset' : 'Reset game'}
          </button>
        </div>
      </main>

      {offlinePopup > 0 && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 flex items-center gap-3 rounded-xl border border-yellow-500/30 bg-gray-900 px-5 py-3 text-sm font-medium text-white shadow-2xl shadow-black/50">
          <span className="text-lg">⚡</span>
          <span>
            Welcome back! Earned <span className="font-bold text-yellow-400">{formatNumber(offlinePopup)}</span> energy while away.
          </span>
          <button
            aria-label="Dismiss"
            className="ml-2 rounded-md bg-gray-800 px-2 py-0.5 text-xs text-gray-400 hover:bg-gray-700 hover:text-white transition-colors"
            onClick={() => setOfflinePopup(0)}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
